import { useEffect, useMemo, useState } from 'react'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectHasUserCompletedOnboardingPhase,
  selectPartitionedGettingStartedUserOnboardingStepData,
} from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import {
  GettingStartedOnboardingStep,
  GettingStartedOnboardingStepId,
  OnboardingStepPhase,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { useGettingStartedOnboardingSteps } from './GettingStarted.hooks'
import { Button, Card, CardColors, Image, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { selectCubkDisplayPhase } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/cubk.selectors'
import { CubkCardState } from '@/features/user/dashboard/onboarding/catch-up-bookkeeping/lib/cubkProgress.types'

// Bookkeeping-first priority order
const newOnboardingPriority: GettingStartedOnboardingStepId[] = [
  GettingStartedOnboardingStep.connectBusinessBankAccount,
  GettingStartedOnboardingStep.uploadBankStatements,
  GettingStartedOnboardingStep.markPersonalTransactions,
  GettingStartedOnboardingStep.clarifyUncategorizedTransactions,
  GettingStartedOnboardingStep.connectHeardBank,
  GettingStartedOnboardingStep.yearEndWrapUpSurvey,
  GettingStartedOnboardingStep.complete_1099_survey,
  GettingStartedOnboardingStep.payrollInterest,
]

export const OnboardingItemList = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      fetchUserOnboardingStepsIfNeeded({
        expanded: true,
        alwaysFetch: true,
      })
    )
  }, [dispatch])

  const partitionedSteps = useReselector(
    selectPartitionedGettingStartedUserOnboardingStepData
  )
  const gettingStartedSteps = useGettingStartedOnboardingSteps()

  // All incomplete items, sorted bookkeeping-first
  const allIncompleteItems = useMemo(() => {
    return partitionedSteps.onboardingSteps
      .filter((step) => !step.completedAt && !step.skippedAt)
      .sort((a, b) => {
        const aIndex = newOnboardingPriority.indexOf(
          a.onboardingStep.identifier
        )
        const bIndex = newOnboardingPriority.indexOf(
          b.onboardingStep.identifier
        )
        return (
          (aIndex === -1 ? Infinity : aIndex) -
          (bIndex === -1 ? Infinity : bIndex)
        )
      })
      .map((step) => gettingStartedSteps[step.onboardingStep.identifier])
      .filter(Boolean)
  }, [partitionedSteps, gettingStartedSteps])

  // Filter completed step IDs to only those present in gettingStartedSteps
  // (hidden steps like JOIN_GROUP_ONBOARDING_SESSION are excluded from the hook's output)
  const visibleCompletedStepIds = useMemo(
    () =>
      partitionedSteps.stepsCompletedOrSkippedIds.filter(
        (stepId) =>
          gettingStartedSteps[stepId] !== null &&
          gettingStartedSteps[stepId] !== undefined
      ),
    [partitionedSteps.stepsCompletedOrSkippedIds, gettingStartedSteps]
  )
  const completedStepCount = visibleCompletedStepIds.length
  const gettingStartedPhaseComplete = useReselector(
    selectHasUserCompletedOnboardingPhase,
    OnboardingStepPhase.gettingStarted
  )
  const [showReviewed, setShowReviewed] = useState(gettingStartedPhaseComplete)

  const cubkDisplayPhase = useReselector(selectCubkDisplayPhase)
  const headerText =
    cubkDisplayPhase === CubkCardState.COMPLETE &&
    allIncompleteItems.length === 0
      ? 'Setup complete'
      : 'Complete your setup'

  const isEmpty = allIncompleteItems.length === 0 && completedStepCount === 0

  if (isEmpty) {
    return (
      <Card
        color={CardColors.LEVEL_0}
        className="mt-0"
        childrenClassName="flex flex-col items-center gap-4 w-full"
      >
        <Text
          variant={TypographyStyles.H1}
          className="text-neutral-900 self-start"
        >
          {headerText}
        </Text>
        <div className="flex flex-col items-center gap-2 py-8">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/confetti-star.svg"
            alt="All done"
            width={64}
            height={64}
            objectFit="contain"
          />
          <Text
            variant={TypographyStyles.LABEL_MD}
            className="text-neutral-900"
          >
            We can tell you like getting things done!
          </Text>
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
            You&apos;re all caught up for now.
          </Text>
        </div>
      </Card>
    )
  }

  return (
    <Card
      color={CardColors.LEVEL_0}
      className="mt-0"
      childrenClassName="flex flex-col items-start gap-4 w-full min-h-full"
    >
      <Text variant={TypographyStyles.H1} className="text-neutral-900">
        {headerText}
      </Text>
      <div className="flex flex-col gap-2 w-full">{allIncompleteItems}</div>
      {completedStepCount > 0 && (
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between w-full">
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowReviewed((v) => !v)}
            >
              {showReviewed ? 'Hide' : 'Show'} {completedStepCount} reviewed
            </Button>
          </div>
          <div
            className={`grid transition-all duration-300 ease-in-out w-full ${showReviewed ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-2 w-full">
                {visibleCompletedStepIds.map(
                  (stepId) => gettingStartedSteps[stepId]
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  )
}
